import {ref} from "vue";
import {storageService} from "@/container";
import {UserSettings} from "@/settings/interfaces";
import {useNotificationStore} from "@/stores/notification.store";
import {
    ScreenContext,
    WindowPoint,
    WindowRectangle,
    expandedBounds,
    NOTIFICATION_PLACEMENT_CHANNEL,
    NOTIFICATION_WINDOW_WIDTH,
    snapPlacement,
} from "@/notification/services/notificationWindowPlacement";
import {supportsWindowPlacement} from "@/core/services/shellCapabilities";

/** Distance in pixels before a press is considered a drag rather than a click. */
const DRAG_THRESHOLD = 5;
/** Interval between two window moves while dragging, in milliseconds. */
const DRAG_TICK_INTERVAL = 16;

/** Opened on the first drop, kept for the lifetime of the notification window. */
let placementChannel: BroadcastChannel | null = null;

/**
 * Make an element a drag handle for the notification window.
 *
 * The whole drag logic runs in the renderer: on each tick it reads the cursor
 * position from the main process, moves the window along, and displays the
 * placeholder of where the window will snap. On release it snaps the window to
 * the nearest left or right screen edge and persists the placement in the user
 * settings. A press without movement is treated as a click, so regular click
 * interactions on the handle (and its children) keep working.
 *
 * @param isOpen - Getter for the drawer open state
 * @param onClick - Called when the press ends without dragging
 * @param onDragStateChange - Called when the drag starts (true) and ends (false)
 */
export default function useNotificationDrag(
    isOpen: () => boolean,
    onClick?: () => void,
    onDragStateChange?: (dragging: boolean) => void
) {
    const store = useNotificationStore();
    const dragging = ref(false);
    let handle: HTMLElement | null = null;
    let pointerId = 0;
    let start = {x: 0, y: 0};
    let open = true;
    let grabOffset: WindowPoint = {x: 0, y: 0};
    let windowSize = {width: NOTIFICATION_WINDOW_WIDTH, height: 0};
    let displayBounds: WindowRectangle | null = null;
    let tickInterval: ReturnType<typeof setInterval> | null = null;
    let ticking = false;
    let dragReady = false;

    /** Swallow the click synthesized right after the drag ends. */
    const suppressClick = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
    };

    const stopTracking = () => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        window.removeEventListener('pointercancel', onPointerCancel);

        if (handle?.hasPointerCapture(pointerId)) {
            handle.releasePointerCapture(pointerId);
        }

        handle = null;
    };

    /**
     * Compute the follow position and the snap target for the current cursor.
     * The target keeps the dragged window size: the expanded panel when the
     * drawer is open, the retracted tab otherwise.
     */
    const dragFrame = (context: ScreenContext) => {
        const bounds: WindowRectangle = {
            x: context.cursor.x - grabOffset.x,
            y: context.cursor.y - grabOffset.y,
            ...windowSize,
        };
        const placement = snapPlacement(context.cursor, bounds, context.display);
        const target = expandedBounds(context.display, placement, windowSize);

        return {bounds, placement, target};
    };

    /**
     * One drag tick: move the window along the cursor and update the placeholder.
     */
    const onDragTick = async () => {
        if (!dragging.value || ticking) {
            return;
        }

        ticking = true;

        try {
            const context = await window.ipcRenderer.getScreenContext!();

            // The drag may have ended while the context was fetched: moving the
            // window now would override the snap position applied by endDrag.
            if (!dragging.value) {
                return;
            }

            const {bounds, placement, target} = dragFrame(context);

            window.ipcRenderer.setNotificationWindowBounds!(bounds);

            // Follow the cursor across displays.
            if (JSON.stringify(displayBounds) !== JSON.stringify(context.display.bounds)) {
                displayBounds = context.display.bounds;
                window.ipcRenderer.dragOverlayShow!(context.display.bounds);
            }

            window.ipcRenderer.dragOverlayUpdate!({
                edge: placement.edge,
                rect: {
                    x: target.x - context.display.bounds.x,
                    y: target.y - context.display.bounds.y,
                    width: target.width,
                    height: target.height,
                },
            });
        } finally {
            ticking = false;
        }
    };

    /**
     * Start the drag: remember the grab point and start the follow loop.
     */
    const startDrag = async () => {
        open = isOpen();

        const [context, bounds] = await Promise.all([
            window.ipcRenderer.getScreenContext!(),
            window.ipcRenderer.getNotificationWindowBounds!(),
        ]);

        // Released while the initialization was in flight: endDrag already ran
        // and hid the overlay — showing it now would leave it up forever.
        if (!dragging.value || !bounds) {
            return;
        }

        grabOffset = {x: context.cursor.x - bounds.x, y: context.cursor.y - bounds.y};
        windowSize = {width: bounds.width, height: bounds.height};
        displayBounds = context.display.bounds;
        dragReady = true;

        window.ipcRenderer.dragOverlayShow!(context.display.bounds);
        tickInterval = setInterval(onDragTick, DRAG_TICK_INTERVAL);
    };

    /**
     * End the drag: snap the window to the nearest edge, persist the placement
     * and keep the drawer state consistent with the new edge.
     */
    const endDrag = async () => {
        if (tickInterval) {
            clearInterval(tickInterval);
            tickInterval = null;
        }

        window.ipcRenderer.dragOverlayHide!();

        // Released before the drag initialization completed: nothing moved yet.
        if (!dragReady) {
            return;
        }

        dragReady = false;

        const context = await window.ipcRenderer.getScreenContext!();
        const {placement, target} = dragFrame(context);

        window.ipcRenderer.setNotificationWindowBounds!(target);

        store.edge = placement.edge;
        store.expandedBounds = open
            ? target
            : expandedBounds(context.display, placement, {
                width: store.expandedBounds?.width ?? NOTIFICATION_WINDOW_WIDTH,
                height: store.expandedBounds?.height ?? target.height,
            });

        const settings = await storageService().read('userSettings') as UserSettings | null;

        if (settings) {
            settings.notification.position = 'custom';
            settings.notification.customPosition = placement;

            await storageService().store('userSettings', settings);

            // Keep an eventually opened settings screen in sync, so it does not
            // overwrite the new placement with stale values on its next save.
            placementChannel ??= new BroadcastChannel(NOTIFICATION_PLACEMENT_CHANNEL);
            placementChannel.postMessage(settings);
        }
    };

    const onPointerMove = (event: PointerEvent) => {
        if (dragging.value || !handle) {
            return;
        }

        // The renderer is deployed independently of the app: never start a drag
        // when running inside an older app without the placement primitives.
        // Dragging also honors the user setting (store.dragEnabled), which is on
        // by default wherever the placement primitives exist.
        if (!supportsWindowPlacement() || !store.dragEnabled) {
            return;
        }

        const distance = Math.hypot(event.screenX - start.x, event.screenY - start.y);

        if (distance > DRAG_THRESHOLD) {
            dragging.value = true;
            window.getSelection()?.removeAllRanges();
            handle.setPointerCapture(pointerId);
            // Hard latch on the store: holds the auto-close countdown against
            // hover changes and fresh detections until the drag ends.
            store.setWindowDragging(true);
            onDragStateChange?.(true);
            startDrag();
        }
    };

    const onPointerUp = () => {
        const wasDragging = dragging.value;

        stopTracking();

        if (wasDragging) {
            // Prevent the click following the pointer release from triggering handle actions.
            window.addEventListener('click', suppressClick, {capture: true, once: true});
            setTimeout(() => window.removeEventListener('click', suppressClick, {capture: true}), 0);

            dragging.value = false;
            store.setWindowDragging(false);
            endDrag();
            onDragStateChange?.(false);
        } else {
            onClick?.();
        }
    };

    const onPointerCancel = () => {
        const wasDragging = dragging.value;

        stopTracking();

        if (wasDragging) {
            dragging.value = false;
            store.setWindowDragging(false);
            endDrag();
            onDragStateChange?.(false);
        }
    };

    const onPointerDown = (event: PointerEvent) => {
        if (event.button !== 0 || handle) {
            return;
        }

        handle = event.currentTarget as HTMLElement;
        pointerId = event.pointerId;
        start = {x: event.screenX, y: event.screenY};

        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('pointercancel', onPointerCancel);
    };

    return {
        dragging,
        onPointerDown,
    };
}
